/**
 * Utility function to sanitize folder names for SharePoint
 *
 * SharePoint has restrictions on characters allowed in folder/file names.
 * This function strips or replaces invalid characters to ensure compatibility.
 */

/**
 * Sanitizes a string to be used as a SharePoint folder name
 *
 * @param name - The original folder name to sanitize
 * @returns A sanitized string safe for use as a SharePoint folder name
 */
export function sanitizeFolderName(name: string): string {
  if (!name || typeof name !== 'string') {
    return '';
  }

  // List of illegal characters in SharePoint files/folders
  const illegalChars = /[~"#%&*:<>?/\\{|}]/g;

  // Replace illegal characters with underscores
  let sanitized = name.replace(illegalChars, '_');

  // Replace multiple consecutive underscores with a single one
  sanitized = sanitized.replace(/_+/g, '_');

  // Remove leading and trailing spaces and dots
  sanitized = sanitized.trim().replace(/^\.+|\.+$/g, '');

  // SharePoint has a 255 character limit for file/folder names
  if (sanitized.length > 255) {
    sanitized = sanitized.substring(0, 255);
  }

  // SharePoint doesn't allow file/folder names to end with these characters
  sanitized = sanitized.replace(/[ .]+$/g, '');

  // If the name is empty after sanitization, provide a default
  if (!sanitized) {
    return 'Untitled_Folder';
  }

  return sanitized;
}
